import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Flame,
  Info,
  TrendingUp,
  Calendar,
  PiggyBank,
  Target,
  CheckCircle,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

const fmt = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);

const fmtShort = (n) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`;
  return fmt(n);
};

// Custom Tooltip for Chart
const CustomTooltip = ({ active, payload, label, fireAge }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isFire = data.age >= fireAge;
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-slate-400 text-xs mb-2">
          Age {label} {isFire && <span className="text-orange-400">• FI</span>}
        </p>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-300">Portfolio:</span>
            <span className="font-semibold text-white">{fmt(data.portfolio)}</span>
          </div>
          {data.portfolio >= data.fireNumber && (
            <p className="text-orange-400 text-xs mt-1">Financially Independent!</p>
          )}
        </div>
      </div>
    );
  }
  return null;
};

// Withdrawal Rate Info
const WITHDRAWAL_RATES = [
  { rate: 3, label: "Conservative", desc: "More cautious, longer runway" },
  { rate: 3.5, label: "Moderate", desc: "Balanced approach" },
  { rate: 4, label: "Standard", desc: "Traditional 4% rule" },
];

export default function FIRECalculator() {
  const [age, setAge] = useState(30);
  const [annualSpending, setAnnualSpending] = useState(30000);
  const [currentInvestments, setCurrentInvestments] = useState(50000);
  const [monthlySavings, setMonthlySavings] = useState(1500);
  const [returnRate, setReturnRate] = useState(5);
  const [withdrawalRate, setWithdrawalRate] = useState(4);
  const [showAssumptions, setShowAssumptions] = useState(false);

  // Calculate FIRE number and projections
  const calculations = useMemo(() => {
    // FIRE number based on 25x rule (or adjusted by withdrawal rate)
    const fireNumber = annualSpending * (100 / withdrawalRate);
    
    // Project wealth over time
    const projections = [];
    let portfolio = currentInvestments;
    let fireAge = null;
    const annualSavings = monthlySavings * 12;
    const monthlyReturn = returnRate / 100 / 12;
    
    // Project from current age to 80
    for (let projAge = age; projAge <= 80; projAge++) {
      // Check if FIRE achieved
      if (portfolio >= fireNumber && fireAge === null) {
        fireAge = projAge;
      }
      
      projections.push({
        age: projAge,
        portfolio: Math.round(portfolio),
        fireNumber: fireNumber,
        isFire: fireAge !== null && projAge >= fireAge,
      });
      
      // If not yet FIRE, continue saving
      if (fireAge === null) {
        // Monthly compounding with contributions
        for (let m = 0; m < 12; m++) {
          portfolio = portfolio * (1 + monthlyReturn) + monthlySavings;
        }
      } else {
        // After FIRE, withdraw and compound (simplified)
        portfolio = portfolio * (1 + returnRate / 100) - annualSpending;
        if (portfolio < 0) portfolio = 0;
      }
    }
    
    // Calculate years to FIRE
    const yearsToFire = fireAge ? fireAge - age : null;
    
    // Calculate savings rate
    const annualIncome = annualSpending + annualSavings;
    const savingsRate = annualIncome > 0 ? (annualSavings / annualIncome) * 100 : 0;
    
    // Determine readiness status
    let status = "on-track";
    let statusMessage = "Your plan looks solid under these assumptions";
    const factors = [];
    
    if (savingsRate >= 50) {
      factors.push("Excellent savings rate (50%+)");
    } else if (savingsRate >= 30) {
      factors.push("Good savings rate (30-50% of income)");
    } else if (savingsRate >= 20) {
      factors.push("Moderate savings rate (20-30%)");
    } else {
      factors.push("Low savings rate — consider increasing");
      status = "needs-attention";
    }
    
    if (yearsToFire && yearsToFire <= 15) {
      factors.push("FIRE achievable within 15 years");
    } else if (yearsToFire && yearsToFire <= 25) {
      factors.push("FIRE within 25 years");
    } else if (yearsToFire) {
      factors.push("Long timeline — consider boosting savings");
      status = "needs-attention";
    } else {
      factors.push("FIRE target not reached by age 80");
      status = "off-track";
      statusMessage = "Consider adjusting your assumptions";
    }
    
    if (returnRate <= 5) {
      factors.push("Conservative return assumption");
    } else if (returnRate <= 7) {
      factors.push("Moderate return assumption");
    } else {
      factors.push("Aggressive return assumption — consider risks");
    }
    
    if (status === "off-track") {
      statusMessage = "Your plan may need adjustments";
    } else if (status === "needs-attention") {
      statusMessage = "Some areas could be improved";
    }
    
    return {
      fireNumber,
      fireAge,
      yearsToFire,
      savingsRate,
      projections,
      status,
      statusMessage,
      factors,
      annualSavings,
    };
  }, [age, annualSpending, currentInvestments, monthlySavings, returnRate, withdrawalRate]);

  const statusColors = {
    "on-track": {
      bg: "bg-emerald-500/10",
      border: "border-emerald-500/30",
      text: "text-emerald-400",
      icon: CheckCircle,
      label: "On Track",
    },
    "needs-attention": {
      bg: "bg-amber-500/10",
      border: "border-amber-500/30",
      text: "text-amber-400",
      icon: AlertTriangle,
      label: "Needs Attention",
    },
    "off-track": {
      bg: "bg-red-500/10",
      border: "border-red-500/30",
      text: "text-red-400",
      icon: AlertTriangle,
      label: "Off Track",
    },
  };

  const currentStatus = statusColors[calculations.status];
  const StatusIcon = currentStatus.icon;

  return (
    <div className="bg-slate-950 min-h-screen pt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back */}
        <Link
          to="/tools"
          data-testid="back-to-tools"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Tools
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
            <Flame className="w-5 h-5 text-orange-400" />
          </div>
          <span className="text-orange-400 text-sm font-medium">Retirement</span>
        </div>
        <h1
          data-testid="fire-calculator-title"
          className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight"
        >
          FIRE Calculator UK
        </h1>
        <p className="text-slate-400 mb-10 max-w-2xl">
          Estimate how much you need to achieve Financial Independence and Retire Early. 
          Based on the 25× rule — adjust the withdrawal rate to model different assumptions.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Inputs */}
          <div className="lg:col-span-5 space-y-6">
            {/* Basic Inputs */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 space-y-5">
              <h2 className="font-outfit font-semibold text-white text-lg">Your Details</h2>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Your age: <span className="text-orange-400 font-medium">{age}</span>
                </label>
                <input
                  type="range"
                  data-testid="input-age"
                  min="18"
                  max="65"
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>18</span>
                  <span>65</span>
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Annual spending (today's money)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-spending"
                    value={annualSpending}
                    onChange={(e) => setAnnualSpending(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 rounded-lg pl-8 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Current investments (combined)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-investments"
                    value={currentInvestments}
                    onChange={(e) => setCurrentInvestments(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 rounded-lg pl-8 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Monthly savings
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-savings"
                    value={monthlySavings}
                    onChange={(e) => setMonthlySavings(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 rounded-lg pl-8 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
                <p className="text-slate-600 text-xs mt-1">
                  {fmt(calculations.annualSavings)}/year • {calculations.savingsRate.toFixed(0)}% savings rate
                </p>
              </div>
            </div>

            {/* Assumptions */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 space-y-5">
              <h2 className="font-outfit font-semibold text-white text-lg">Assumptions</h2>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Long-term return assumption: <span className="text-orange-400 font-medium">{returnRate}%</span>
                </label>
                <input
                  type="range"
                  data-testid="input-return"
                  min="3"
                  max="12"
                  step="0.5"
                  value={returnRate}
                  onChange={(e) => setReturnRate(Number(e.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>3%</span>
                  <span>12%</span>
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-3">
                  Withdrawal rate assumption
                </label>
                <div className="space-y-2">
                  {WITHDRAWAL_RATES.map((wr) => (
                    <button
                      key={wr.rate}
                      data-testid={`withdrawal-rate-${wr.rate}`}
                      onClick={() => setWithdrawalRate(wr.rate)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200 ${
                        withdrawalRate === wr.rate
                          ? "bg-orange-500/10 border-orange-500/50 text-white"
                          : "bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-600"
                      }`}
                    >
                      <div className="text-left">
                        <span className="font-medium">{wr.rate}%</span>
                        <span className="text-slate-500 text-sm ml-2">— {wr.label}</span>
                      </div>
                      {withdrawalRate === wr.rate && (
                        <CheckCircle className="w-4 h-4 text-orange-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-7 space-y-6">
            {/* FIRE Number */}
            <div className="bg-orange-500/5 border border-orange-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-orange-400" />
                <p className="text-slate-400 text-sm">Estimated FIRE Number</p>
              </div>
              <p data-testid="fire-number" className="font-outfit text-4xl sm:text-5xl font-bold text-orange-400">
                {fmt(calculations.fireNumber)}
              </p>
              <p className="text-slate-500 text-sm mt-2">
                To generate {fmt(annualSpending)} per year using a {withdrawalRate}% withdrawal rate
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <p className="text-slate-500 text-xs">Projected years</p>
                </div>
                <p data-testid="years-to-fire" className="font-outfit text-3xl font-bold text-white">
                  {calculations.yearsToFire ?? "—"}
                </p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-slate-500" />
                  <p className="text-slate-500 text-xs">Projected FIRE age</p>
                </div>
                <p data-testid="fire-age" className="font-outfit text-3xl font-bold text-white">
                  {calculations.fireAge ?? "80+"}
                </p>
              </div>
            </div>

            {/* Status */}
            <div className={`${currentStatus.bg} border ${currentStatus.border} rounded-2xl p-5`}>
              <div className="flex items-center gap-2 mb-2">
                <StatusIcon className={`w-5 h-5 ${currentStatus.text}`} />
                <p className={`font-semibold ${currentStatus.text}`}>
                  FIRE Readiness: {currentStatus.label}
                </p>
              </div>
              <p className="text-slate-400 text-sm mb-3">{calculations.statusMessage}</p>
              <div className="space-y-1.5">
                <p className="text-slate-500 text-xs font-medium">Contributing factors:</p>
                <ul className="space-y-1">
                  {calculations.factors.map((factor, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-400 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Timeline Chart */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-outfit font-semibold text-white text-lg">Your FIRE Timeline</h3>
                  <p className="text-slate-500 text-sm">
                    Projected wealth growth from age {age} to 80
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1.5 rounded-full bg-orange-500 inline-block" />
                    <span className="text-slate-400">Working & Saving</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    <span className="text-slate-400">Financially Independent</span>
                  </div>
                </div>
              </div>

              {/* SVG Gradient Defs */}
              <svg width="0" height="0" style={{ position: "absolute" }}>
                <defs>
                  <linearGradient id="gradPortfolio" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
              </svg>

              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={calculations.projections}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis
                    dataKey="age"
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "#1e293b" }}
                  />
                  <YAxis
                    tickFormatter={(v) => fmtShort(v)}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                  />
                  <Tooltip content={<CustomTooltip fireAge={calculations.fireAge} />} />
                  
                  {/* FIRE Number reference line */}
                  <ReferenceLine
                    y={calculations.fireNumber}
                    stroke="#f97316"
                    strokeDasharray="5 5"
                    strokeOpacity={0.5}
                  />
                  
                  {/* FIRE Age reference line */}
                  {calculations.fireAge && (
                    <ReferenceLine
                      x={calculations.fireAge}
                      stroke="#10b981"
                      strokeDasharray="5 5"
                      strokeOpacity={0.7}
                    />
                  )}

                  <Area
                    type="monotone"
                    dataKey="portfolio"
                    stroke="#f97316"
                    strokeWidth={2.5}
                    fill="url(#gradPortfolio)"
                    dot={false}
                    activeDot={{ r: 5, fill: "#f97316", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Chart Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-800 text-xs text-slate-500">
                <span>Dashed orange line = FIRE target ({fmtShort(calculations.fireNumber)})</span>
                {calculations.fireAge && (
                  <span>Dashed green line = FIRE age ({calculations.fireAge})</span>
                )}
              </div>
            </div>

            {/* Assumptions Accordion */}
            <button
              onClick={() => setShowAssumptions(!showAssumptions)}
              className="w-full flex items-center justify-between bg-slate-900/30 border border-slate-800/50 rounded-xl p-4 text-left hover:bg-slate-900/50 transition-colors"
            >
              <span className="text-slate-400 text-sm font-medium">
                View calculation assumptions and methodology
              </span>
              {showAssumptions ? (
                <ChevronUp className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              )}
            </button>

            {showAssumptions && (
              <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-5 space-y-3">
                <h4 className="text-white font-medium text-sm">Important Limitations</h4>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-2 flex-shrink-0" />
                    This is a foundational estimate using the 25× rule (adjusted by withdrawal rate)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-2 flex-shrink-0" />
                    Real outcomes depend on tax, fees, market volatility, and inflation
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-2 flex-shrink-0" />
                    State Pension is not included in this calculation
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-2 flex-shrink-0" />
                    Assumes constant returns (real markets fluctuate)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-2 flex-shrink-0" />
                    For personalised advice, speak with an FCA-authorised financial adviser
                  </li>
                </ul>
              </div>
            )}

            {/* Info Box */}
            <div className="flex items-start gap-2 bg-slate-900/30 border border-slate-800/50 rounded-xl p-4">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-500 text-xs leading-relaxed">
                FIRE (Financial Independence, Retire Early) is based on the principle that if you save 25× your annual expenses, 
                you can withdraw 4% per year indefinitely. Adjust the withdrawal rate for more conservative or aggressive assumptions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
